import { Route } from "../../../../../utils/models/Route";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { MarkerFeature } from "../../../../../utils/models/MarkerFeature";

/**
 * Validates and fixes route data integrity issues
 */
export const routeValidationActions = {
  /**
   * Validate that a route has the correct number of linestrings for its markers
   * For N markers, there should be N-1 linestrings connecting them
   */
  validateRouteIntegrity(route: Route): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    const markerCount = route.markers.length;
    const lineStringCount = route.lineStrings.length;
    
    // Check basic count relationship
    if (markerCount === 0) {
      if (lineStringCount > 0) {
        issues.push(`Route has ${lineStringCount} linestrings but no markers`);
      }
    } else if (markerCount === 1) {
      if (lineStringCount > 0) {
        issues.push(`Route has ${lineStringCount} linestrings but only 1 marker`);
      }
    } else {
      // For 2+ markers, should have exactly N-1 linestrings
      const expectedLineStrings = markerCount - 1;
      if (lineStringCount !== expectedLineStrings) {
        issues.push(`Route has ${markerCount} markers but ${lineStringCount} linestrings (expected ${expectedLineStrings})`);
      }
    }
    
    // Check for orphaned linestrings (very short segments that don't connect markers)
    route.lineStrings.forEach((lineString, index) => {
      const coords = lineString.lineStringGeometry.coordinates;
      if (coords.length < 2) {
        issues.push(`LineString at index ${index} has fewer than 2 coordinates`);
      }
      
      // Check if linestring is suspiciously short (less than 10 meters)
      const distance = lineString.lineStringProperties?.summary?.distance || 0;
      if (distance > 0 && distance < 10) {
        issues.push(`LineString at index ${index} is only ${distance.toFixed(2)}m long (possibly orphaned)`);
      }
    });
    
    // Validate marker-linestring connectivity
    if (markerCount >= 2 && lineStringCount > 0) {
      // Check that linestrings connect consecutive markers
      for (let i = 0; i < markerCount - 1; i++) {
        if (i < lineStringCount) {
          const marker1 = route.markers[i];
          const marker2 = route.markers[i + 1];
          const lineString = route.lineStrings[i];
          
          const marker1Coords = marker1.markerGeometry.coordinates;
          const marker2Coords = marker2.markerGeometry.coordinates;
          const lineCoords = lineString.lineStringGeometry.coordinates;
          
          if (lineCoords.length >= 2) {
            const lineStart = lineCoords[0];
            const lineEnd = lineCoords[lineCoords.length - 1];
            
            // Check if linestring connects the markers (with small tolerance for rounding)
            const startMatches = Math.abs(lineStart[0] - marker1Coords[0]) < 0.0001 && 
                               Math.abs(lineStart[1] - marker1Coords[1]) < 0.0001;
            const endMatches = Math.abs(lineEnd[0] - marker2Coords[0]) < 0.0001 && 
                             Math.abs(lineEnd[1] - marker2Coords[1]) < 0.0001;
            
            if (!startMatches || !endMatches) {
              issues.push(`LineString ${i} doesn't properly connect markers ${i} and ${i + 1}`);
            }
          }
        }
      }
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  },
  
  /**
   * Removes orphaned linestrings from a route
   */
  cleanupOrphanedLineStrings(route: Route): { removed: number; route: Route } {
    const validation = this.validateRouteIntegrity(route);
    let removed = 0;
    
    if (!validation.isValid) {
      const markerCount = route.markers.length;
      const expectedLineStrings = Math.max(0, markerCount - 1);
      
      // Remove excess linestrings
      if (route.lineStrings.length > expectedLineStrings) {
        const excess = route.lineStrings.length - expectedLineStrings;
        route.lineStrings.splice(expectedLineStrings, excess);
        removed = excess;
      }
      
      // Update route timestamp
      route.updated_at = new Date();
    }
    
    return { removed, route };
  },
  
  /**
   * Rebuilds linestrings to ensure they properly connect all markers
   */
  async rebuildLineStrings(route: Route, snapToRoads: boolean = true): Promise<Route> {
    // Clear existing linestrings
    route.lineStrings = [];
    
    // If we have 2+ markers, create linestrings between them
    if (route.markers.length >= 2) {
      for (let i = 0; i < route.markers.length - 1; i++) {
        const startMarker = route.markers[i];
        const endMarker = route.markers[i + 1];
        
        const startCoords = startMarker.markerGeometry.coordinates;
        const endCoords = endMarker.markerGeometry.coordinates;
        
        // Create a simple straight-line connection for now
        // In a real implementation, this would call the routing service
        const lineString = LineStringFeature.coordsOnlyFeature([
          [...startCoords],
          [...endCoords]
        ]);
        
        route.lineStrings.push(lineString);
      }
    }
    
    route.updated_at = new Date();
    return route;
  }
};